import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ordersAPI, usersAPI, productsAPI } from '../../services/api';
import { useCompany } from '../CompanyContext';
import { useUser } from '../UserContext';
import { Order, CreateOrderDto, UpdateOrderDto, OrderStatus, OrderType, User, Product, Customer } from '../../types';
import CustomerPicker from './CustomerPicker';
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  CheckIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import { toastSuccess } from '../../utils/toast';
import { useTranslation } from 'react-i18next';

const OrderForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { selectedCompany } = useCompany();
  const { currentUser } = useUser();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [isEditing, setIsEditing] = useState(false);

  const [formData, setFormData] = useState<CreateOrderDto>({
    companyId: selectedCompany?.id || '',
    assignedTo: '',
    customerId: undefined,
    status: OrderStatus.ASSIGNED,
    type: OrderType.ONE_TIME,
    subtotal: 0,
    tax: 0,
    discount: 0,
    currencyCode: 'USD',
    exchangeRate: 1.0,
    assignedDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    notes: '',
    items: [],
  });

  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  const [completionData, setCompletionData] = useState({
    employeeNotes: '',
    actualSubtotal: 0,
    actualTax: 0,
    actualDiscount: 0,
  });

  const [newItem, setNewItem] = useState({
    productId: '',
    quantity: 1,
    unitPrice: 0,
    discount: 0,
    notes: '',
  });

  useEffect(() => {
    if (selectedCompany) {
      setFormData(prev => ({ ...prev, companyId: selectedCompany.id }));
      loadFormData();
      loadUsers();
      loadProducts();
    }
  }, [selectedCompany, id]);

  // Auto-populate unit price when product is selected
  useEffect(() => {
    if (newItem.productId) {
      const product = products.find(p => p.id === newItem.productId);
      if (product && product.price > 0) {
        setNewItem(prev => ({ ...prev, unitPrice: product.price }));
      }
    }
  }, [newItem.productId, products]);

  const loadFormData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const order = await ordersAPI.getById(id);
      setFormData({
        companyId: order.companyId,
        assignedTo: order.assignedTo,
        customerId: order.customerId,
        customerName: order.customerName || '',
        status: order.status,
        type: order.type,
        subtotal: Number(order.subtotal ?? 0),
        tax: Number(order.tax ?? 0),
        discount: Number(order.discount ?? 0),
        currencyCode: order.currencyCode || 'USD',
        exchangeRate: Number(order.exchangeRate ?? 1),
        assignedDate: new Date(order.assignedDate).toISOString().split('T')[0],
        dueDate: order.dueDate ? new Date(order.dueDate).toISOString().split('T')[0] : undefined,
        notes: order.notes || '',
        items: order.items?.map(item => ({
          productId: item.productId,
          quantity: Number(item.quantity ?? 0),
          unitPrice: Number(item.unitPrice ?? 0),
          discount: Number(item.discount ?? 0),
          notes: item.notes || '',
        })) || [],
      });

      // Set completion data if order is completed
      if (order.status === OrderStatus.COMPLETED) {
        setCompletionData({
          employeeNotes: order.employeeNotes || '',
          actualSubtotal: order.subtotal,
          actualTax: order.tax,
          actualDiscount: order.discount,
        });
      }
    } catch (err) {
      setError('Failed to load order');
      console.error('Error loading order:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const usersData = await usersAPI.getByCompany(selectedCompany!.id);
      setUsers(usersData);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const loadProducts = async () => {
    try {
      const productsData = await productsAPI.getByCompany(selectedCompany!.id);
      setProducts(productsData);
    } catch (err) {
      console.error('Error loading products:', err);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCustomerSelect = (customer: Customer | null) => {
    setSelectedCustomer(customer);
    if (customer) {
      setFormData(prev => ({
        ...prev,
        customerId: customer.id,
        customerName: customer.name,
        customerEmail: customer.email || '',
        customerPhone: customer.phone || '',
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        customerId: undefined,
        customerName: '',
        customerEmail: '',
        customerPhone: '',
      }));
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: new Date(value).toISOString(),
    }));
  };

  const handleCompletionChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setCompletionData(prev => ({
      ...prev,
      [name]: name.includes('actual') ? parseFloat(value) || 0 : value,
    }));
  };

  const handleAddItem = () => {
    if (!newItem.productId || newItem.quantity <= 0) return;

    const product = products.find(p => p.id === newItem.productId);
    if (!product) return;

    // Use the manually entered unit price or fall back to product price
    const unitPrice = newItem.unitPrice > 0 ? newItem.unitPrice : product.price;
    
    if (unitPrice <= 0) {
      setError('Unit price must be greater than 0');
      return;
    }

    const item = {
      productId: newItem.productId,
      quantity: newItem.quantity,
      unitPrice: unitPrice,
      discount: newItem.discount || 0,
      notes: newItem.notes || '',
    };

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, item],
    }));

    // Reset new item form
    setNewItem({
      productId: '',
      quantity: 1,
      unitPrice: 0,
      discount: 0,
      notes: '',
    });

    // Recalculate totals
    recalculateTotals([...formData.items, item]);
  };

  const handleRemoveItem = (index: number) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      items: newItems,
    }));
    recalculateTotals(newItems);
  };

  const recalculateTotals = (items: any[]) => {
    const subtotal = items.reduce((sum, item) => {
      const itemTotal = (item.unitPrice * item.quantity) - (item.discount || 0);
      return sum + itemTotal;
    }, 0);

    const tax = subtotal * 0.1; // 10% tax
    const total = subtotal + tax - (formData.discount || 0);

    setFormData(prev => ({
      ...prev,
      subtotal,
      tax,
    }));
  };

  const validateForm = () => {
    if (!formData.assignedTo) {
      setError('Please select an employee to assign the order to');
      return false;
    }

    if (formData.items.length === 0) {
      setError('Please add at least one item to the order');
      return false;
    }

    // Validate each item
    for (let i = 0; i < formData.items.length; i++) {
      const item = formData.items[i];
      if (item.unitPrice <= 0) {
        setError(`Item ${i + 1}: Unit price must be greater than 0`);
        return false;
      }
      if (item.quantity <= 0) {
        setError(`Item ${i + 1}: Quantity must be greater than 0`);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    // Validate form before submission
    if (!validateForm()) {
      setSaving(false);
      return;
    }

    // Ensure number fields are properly typed before sending to API
    const prepareDataForAPI = (data: any) => {
      const prepared = { ...data };
      
      // Ensure order-level number fields are numbers
      if (typeof prepared.subtotal === 'string') {
        prepared.subtotal = prepared.subtotal === '' ? 0 : parseFloat(prepared.subtotal);
      }
      if (typeof prepared.tax === 'string') {
        prepared.tax = prepared.tax === '' ? 0 : parseFloat(prepared.tax);
      }
      if (typeof prepared.discount === 'string') {
        prepared.discount = prepared.discount === '' ? 0 : parseFloat(prepared.discount);
      }
      if (typeof prepared.exchangeRate === 'string') {
        prepared.exchangeRate = prepared.exchangeRate === '' ? 1 : parseFloat(prepared.exchangeRate);
      }
      
      // Convert date strings to ISO strings for API
      if (prepared.assignedDate) {
        prepared.assignedDate = new Date(prepared.assignedDate).toISOString();
      }
      if (prepared.dueDate) {
        prepared.dueDate = new Date(prepared.dueDate).toISOString();
      }
      
      // Ensure item-level number fields are numbers
      if (prepared.items && Array.isArray(prepared.items)) {
        prepared.items = prepared.items.map((item: any) => ({
          ...item,
          quantity: typeof item.quantity === 'string' ? parseInt(item.quantity) || 1 : item.quantity,
          unitPrice: typeof item.unitPrice === 'string' ? parseFloat(item.unitPrice) || 0 : item.unitPrice,
          discount: typeof item.discount === 'string' ? parseFloat(item.discount) || 0 : item.discount,
        }));
      }
      
      // Debug: Log the prepared data to verify types
      console.log('Prepared order data for API:', {
        subtotal: prepared.subtotal,
        subtotalType: typeof prepared.subtotal,
        tax: prepared.tax,
        taxType: typeof prepared.tax,
        items: prepared.items?.map((item: any) => ({
          unitPrice: item.unitPrice,
          unitPriceType: typeof item.unitPrice,
          quantity: item.quantity,
          quantityType: typeof item.quantity,
        })),
      });
      
      return prepared;
    };

    try {
      if (id) {
        // Update existing order
        const updateData: UpdateOrderDto = {
          assignedTo: formData.assignedTo,
          customerId: formData.customerId,
          status: formData.status,
          type: formData.type,
          subtotal: formData.subtotal,
          tax: formData.tax,
          discount: formData.discount,
          dueDate: formData.dueDate,
          notes: formData.notes,
        };
        await ordersAPI.update(id, prepareDataForAPI(updateData));
      } else {
        // Create new order - backend will set assignedBy automatically
        await ordersAPI.create(prepareDataForAPI(formData));
      }
      toastSuccess(id ? 'Order updated successfully' : 'Order created successfully');
      navigate('/orders');
    } catch (err: any) {
      console.error('Error saving order:', err);
      
      // Handle backend validation errors
      if (err.response?.data?.message) {
        const errorData = err.response.data;
        
        // If it's an array of validation errors
        if (Array.isArray(errorData.message)) {
          const errorMessages = errorData.message.map((errorMsg: string) => {
            // Parse validation error messages like "items.0.unitPrice must not be less than 0"
            const itemMatch = errorMsg.match(/^items\.(\d+)\.(\w+)\s+(.+)$/);
            if (itemMatch) {
              const [, itemIndex, field, message] = itemMatch;
              return `Item ${parseInt(itemIndex) + 1} - ${field}: ${message}`;
            }
            return errorMsg;
          });
          setError(errorMessages.join(', '));
        } else {
          // Single error message
          setError(errorData.message);
        }
      } else {
        setError('Failed to save order');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteOrder = async () => {
    if (!id) return;
    
    setCompleting(true);
    setError(null);

    try {
      await ordersAPI.completeOrder(id, completionData);
      await loadFormData(); // Reload to get updated status
      setIsEditing(false);
      toastSuccess('Order completed successfully');
    } catch (err) {
      setError('Failed to complete order');
      console.error('Error completing order:', err);
    } finally {
      setCompleting(false);
    }
  };

  const canComplete = id && formData.status === OrderStatus.IN_PROGRESS && 
    (currentUser?.id === formData.assignedTo || currentUser?.role === 'admin' || currentUser?.role === 'owner');

  const isCompleted = formData.status === OrderStatus.COMPLETED;

  if (!selectedCompany) {
    return (
      <div className="text-center py-12">
        <h3 className="mt-2 text-sm font-medium text-gray-900">No company selected</h3>
        <p className="mt-1 text-sm text-gray-500">Please select a company to create an order.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/orders')}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-all duration-200"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {id ? (isCompleted ? t('orders.form.titleViewCompleted') : t('orders.form.titleEdit')) : t('orders.form.titleCreate')}
            </h1>
            <p className="text-gray-600">{t('orders.form.manageFor', { company: selectedCompany.name })}</p>
            {id && (
              <div className="flex items-center space-x-2 mt-1">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  formData.status === OrderStatus.COMPLETED 
                    ? 'bg-green-100 text-green-800'
                    : formData.status === OrderStatus.IN_PROGRESS
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-blue-100 text-blue-800'
                }`}>
                  {(formData.status || OrderStatus.ASSIGNED).replace('_', ' ').toUpperCase()}
                </span>
                {isCompleted && (
                  <span className="text-sm text-gray-500">
                    Completed by {users.find(u => u.id === formData.assignedTo)?.firstName} {users.find(u => u.id === formData.assignedTo)?.lastName}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        {id && !isCompleted && (
          <div className="flex space-x-2">
            {canComplete && (
              <button
                onClick={() => setIsEditing(!isEditing)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {isEditing ? 'Cancel Edit' : 'Edit Order'}
              </button>
            )}
            {canComplete && (
              <button
                onClick={handleCompleteOrder}
                disabled={completing}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
              >
                {completing ? 'Completing...' : 'Complete Order'}
              </button>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">{error}</div>
        </div>
      )}

      {/* Order Completion Section */}
      {id && canComplete && isEditing && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-yellow-900 mb-4 flex items-center">
            <CheckIcon className="h-5 w-5 mr-2" />
            Complete Order
          </h3>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-yellow-700">Employee Notes</label>
              <textarea
                name="employeeNotes"
                value={completionData.employeeNotes}
                onChange={handleCompletionChange}
                rows={3}
                className="mt-1 block w-full border-yellow-300 rounded-md shadow-sm focus:ring-yellow-500 focus:border-yellow-500"
                placeholder="Add any notes about the completion..."
              />
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-yellow-700">Actual Subtotal</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  name="actualSubtotal"
                  value={completionData.actualSubtotal}
                  onChange={handleCompletionChange}
                  className="mt-1 block w-full border-yellow-300 rounded-md shadow-sm focus:ring-yellow-500 focus:border-yellow-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-yellow-700">Actual Tax</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  name="actualTax"
                  value={completionData.actualTax}
                  onChange={handleCompletionChange}
                  className="mt-1 block w-full border-yellow-300 rounded-md shadow-sm focus:ring-yellow-500 focus:border-yellow-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-yellow-700">Actual Discount</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  name="actualDiscount"
                  value={completionData.actualDiscount}
                  onChange={handleCompletionChange}
                  className="mt-1 block w-full border-yellow-300 rounded-md shadow-sm focus:ring-yellow-500 focus:border-yellow-500"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Completed Order Summary */}
      {id && isCompleted && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-green-900 mb-4 flex items-center">
            <CheckIcon className="h-5 w-5 mr-2" />
            Order Completed
          </h3>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div>
              <h4 className="font-medium text-green-800 mb-2">Employee Notes</h4>
              <p className="text-green-700">{completionData.employeeNotes || 'No notes provided'}</p>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-green-700">Actual Subtotal:</span>
                <span className="font-medium text-green-800">${completionData.actualSubtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-700">Actual Tax:</span>
                <span className="font-medium text-green-800">${completionData.actualTax.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-700">Actual Discount:</span>
                <span className="font-medium text-green-800">-${completionData.actualDiscount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-bold border-t pt-2">
                <span className="text-green-800">Actual Total:</span>
                <span className="text-green-800">${(completionData.actualSubtotal + completionData.actualTax - completionData.actualDiscount).toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Order Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('orders.form.sections.details')}</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('orders.form.type')}</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  disabled={isCompleted}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                >
                  <option value={OrderType.ONE_TIME}>{t('orders.types.ONE_TIME')}</option>
                </select>
              </div>



              <div>
                <label className="block text-sm font-medium text-gray-700">{t('orders.form.status')}</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  disabled={isCompleted}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                >
                  <option value={OrderStatus.ASSIGNED}>{t('orders.statuses.assigned')}</option>
                  <option value={OrderStatus.IN_PROGRESS}>{t('orders.statuses.in_progress')}</option>
                  <option value={OrderStatus.COMPLETED}>{t('orders.statuses.completed')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">{t('orders.form.assignedTo')}</label>
                <select
                  name="assignedTo"
                  value={formData.assignedTo}
                  onChange={handleInputChange}
                  required
                  disabled={isCompleted}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                >
                  <option value="">{t('orders.form.selectEmployee')}</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName} ({user.role})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">{t('orders.form.assignedDate')}</label>
                <input
                  type="date"
                  name="assignedDate"
                  value={formData.assignedDate || ''}
                  onChange={handleDateChange}
                  required
                  disabled={isCompleted}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">{t('orders.form.dueDate')}</label>
                <input
                  type="date"
                  name="dueDate"
                  value={formData.dueDate || ''}
                  onChange={handleDateChange}
                  disabled={isCompleted}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                />
              </div>
            </div>
          </div>

          {/* Customer Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('orders.form.sections.customer')}</h3>
            
            <div className="space-y-4">
              <CustomerPicker
                companyId={selectedCompany!.id}
                selectedCustomerId={formData.customerId}
                onCustomerSelect={handleCustomerSelect}
                disabled={isCompleted}
              />

              <div>
                <label className="block text-sm font-medium text-gray-700">{t('orders.form.notes')}</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={3}
                  disabled={isCompleted}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Order Items */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{t('orders.form.sections.items')}</h3>
          
          {/* Add New Item - Only show if not completed */}
          {!isCompleted && (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-6 mb-6 p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('orders.form.product')}</label>
                <select
                  value={newItem.productId}
                  onChange={(e) => setNewItem(prev => ({ ...prev, productId: e.target.value }))}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">{t('orders.form.selectProduct')}</option>
                  {products.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.name} - ${product.price}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">{t('orders.form.quantity')}</label>
                <input
                  type="number"
                  min="1"
                  value={newItem.quantity}
                  onChange={(e) => setNewItem(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">{t('orders.form.unitPrice')}</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newItem.unitPrice}
                  onChange={(e) => setNewItem(prev => ({ ...prev, unitPrice: parseFloat(e.target.value) || 0 }))}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">{t('orders.form.discount')}</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newItem.discount}
                  onChange={(e) => setNewItem(prev => ({ ...prev, discount: parseFloat(e.target.value) || 0 }))}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">{t('orders.form.itemNotes')}</label>
                <input
                  type="text"
                  value={newItem.notes}
                  onChange={(e) => setNewItem(prev => ({ ...prev, notes: e.target.value }))}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="flex items-end">
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="w-full bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <PlusIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* Items List */}
          {formData.items.length > 0 ? (
            <div className="space-y-2">
              {formData.items.map((item, index) => {
                const product = products.find(p => p.id === item.productId);
                const itemTotal = (item.unitPrice * item.quantity) - (item.discount || 0);
                
                return (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium">{product?.name || 'Unknown Product'}</p>
                      <p className="text-sm text-gray-500">
                        Qty: {item.quantity} × ${item.unitPrice} - ${item.discount || 0} discount
                      </p>
                      {item.notes && <p className="text-sm text-gray-500">Notes: {item.notes}</p>}
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium">${itemTotal.toFixed(2)}</span>
                      {!isCompleted && (
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No items added to this order.</p>
          )}
        </div>

        {/* Order Summary */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{t('orders.form.sections.summary')}</h3>
          
          <div className="space-y-2">
            <div className="flex justify-between">
              <span>{t('orders.form.subtotal')}:</span>
              <span>${Number(formData.subtotal ?? 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>{t('orders.form.tax')}:</span>
              <span>${Number(formData.tax ?? 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>{t('orders.form.discount')}:</span>
              <span>-${Number(formData.discount ?? 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-bold text-lg border-t pt-2">
              <span>{t('orders.form.total')}:</span>
              <span>${(Number(formData.subtotal ?? 0) + Number(formData.tax ?? 0) - Number(formData.discount ?? 0)).toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        {!isCompleted && (
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/orders')}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {saving ? t('common.saving') : (id ? t('orders.form.update') : t('orders.form.create'))}
            </button>
          </div>
        )}

        {/* Generate Sale Button for Completed Orders */}
        {isCompleted && (
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/orders')}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              Back to Orders
            </button>
            <button
              type="button"
              onClick={() => navigate(`/sales/new?orderId=${id}`)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <CurrencyDollarIcon className="h-4 w-4 mr-2 inline" />
              Generate Sale
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default OrderForm;
